import { DumpsterSize } from "./Order";

export type FleetOptimizationStatus = "pending" | "running" | "complete" | "failed";
export type StopType = "driver_start" | "delivery" | "pickup" | "dump_yard" | "storage";

export interface OptimizedStop {
  type: StopType;
  id: string;
  lat?: number; // per-stop coords (added later; absent on older runs)
  lng?: number;
  legMeters: number;
  legSeconds: number;
  serviceSeconds: number;
  arrivalSeconds: number;
  departureSeconds: number;
  eta: string; // "HH:MM"
  note?: string; // action taken at the stop, e.g. storage "swap 10 -> 15"
  dumpFee?: number; // estimated dump fee incurred at this stop (dump yards)
  // Storage stops only: the yard's empties by size (e.g. { "10": 3 }) just before
  // and after this stop — the inventory timeline as empties deplete across the day.
  inventoryBefore?: Record<string, number>;
  inventoryAfter?: Record<string, number>;
}

// Why a route could not be driven: the stop it got stuck on, what it needed, and
// the fleet-shared storage inventory at that moment. Present only on infeasible routes.
export interface RouteInfeasibility {
  stopId: string;
  stopType: StopType;
  neededSize?: string; // empty size that wasn't available
  wasteType?: string;
  detail: string;
  storages: { id: string; counts: Record<string, number> }[]; // per-yard empties at the failure
}

export interface Penalty {
  type: string; // e.g. "compactness"
  amount: number;
}

export interface CostBreakdown {
  fuelCost: number;
  wageCost: number;
  dumpFees: number;
  maintenanceCost?: number; // added later; absent on older runs
  penalties?: Penalty[]; // soft penalties by type (compactness, ...)
  cost: number;
  meters: number;
  drivingSeconds: number;
  serviceSeconds: number;
  totalSeconds: number;
}

export interface OptimizedRoute extends CostBreakdown {
  driverId: string;
  vehicleId: string;
  feasible: boolean;
  stops: OptimizedStop[];
  infeasibility?: RouteInfeasibility; // why, when feasible === false
}

export interface OptimizedPlan extends CostBreakdown {
  routes: OptimizedRoute[];
}

export interface FleetOptimizationInput {
  date: string;
  driverAssignments: {
    driverId: string;
    vehicleId: string;
    startingDumpster?: { size: DumpsterSize; state: "empty" | "full" };
    startTime?: string; // shift-start override "HH:MM" (baseline for ETAs; else driver's stored start)
    endTime?: string; // hard shift-end "HH:MM" (soft-enforced via endTimePenalty)
  }[];
  // Optional optimizer tuning (omitted → backend defaults).
  dumpFeeRankWeight?: number; // weight (0,1] controlling dump-yard selection: lower = prefer closer yards (fewer miles); 1.0 = current default behavior
  compactnessPenalty?: number; // $ per mile of route spread (tighter territories)
  maintenancePerMile?: number; // $ per mile vehicle-maintenance override (0 disables)
  overtimeAllowed?: boolean; // allow routes over 8h
  overtimePenalty?: number; // $ per overtime-hour when overtime not allowed
  endTimePenalty?: number; // $ per hour a driver is past their end time
  allowCarryover?: boolean; // may end the day carrying a full container
  carryoverThreshold?: number; // miles out-of-the-way before carrying over
  maxCarryovers?: number; // hard fleet-wide cap on trucks ending full (0/unset = unlimited)
  // Desired per-job service windows (keyed by delivery/pickup id); a stop serviced
  // outside its own window incurs timeWindowPenalty per hour outside. A delivery and a
  // pickup of the same order carry independent windows.
  timeWindows?: { jobId: string; start: string; end: string }[]; // "HH:MM"
  timeWindowPenalty?: number; // $ per hour a stop is outside its own window
  balancePenalty?: number; // fleet-level soft penalty pushing toward even driver days
  // Per-job overrides.
  locks?: { jobId: string; driverId: string }[]; // pin a job to a driver
  exclude?: string[]; // delivery/pickup ids to drop from the run
  allowTopoffForJobs?: string[]; // delivery ids allowed to be topped off
  // Override a pickup's waste type for this run (decides which dump yards its full
  // container may use); pickups not listed keep their stored type.
  pickupWasteTypes?: { pickupId: string; wasteType: string }[];
  // Per-pickup billed dump-weight override (tons, > 0), used instead of the
  // size-average when that pickup's container is dumped; pickups not listed use
  // the size-average.
  pickupDumpWeights?: { pickupId: string; weight: number }[];
  // Per-pickup forced dump yard (a dated dump-yard id for the run date); overrides
  // cheapest-yard selection and the yard's waste filter. Pickups not listed pick the
  // cheapest feasible yard.
  pickupDumpYards?: { pickupId: string; dumpYardId: string }[];
  // Force one job to be serviced after another on the SAME truck for this run
  // (precedence): afterJobId is serviced later, beforeJobId earlier, and the pair is
  // pinned to one vehicle — e.g. pick up the old dumpster (before) before delivering
  // the new one (after) at a tight, no-maneuver address. A link naming a job not in
  // the run, or with after === before, is ignored by the optimizer.
  precedences?: { afterJobId: string; beforeJobId: string }[];
  // Same-unit swap: bind a customer-owned box's redelivery to be the very next
  // customer job after its pickup, on the same truck, so the exact box picked up is
  // returned (a dump yard between them is fine). pickupJobId is removed first,
  // deliveryJobId returns the same box. A link naming a job not in the run, not a
  // pickup+delivery pair, or size-mismatched, is ignored by the optimizer.
  swaps?: { pickupJobId: string; deliveryJobId: string }[];
}

// Telemetry about the search that produced the options — chiefly how long it ran.
export interface SolveStats {
  durationMs: number; // wall clock of the solve phase
  workers?: number; // parallel LNS searches
  totalIterations?: number; // summed across workers
  solveBudgetMs?: number; // configured time budget (0 = none)
}

export interface FleetOptimization {
  id: string;
  date: string;
  status: FleetOptimizationStatus;
  createdAt: string;
  input: FleetOptimizationInput;
  result?: { options: OptimizedPlan[]; solve?: SolveStats };
  error?: string;
}

// A plan is infeasible when the solver couldn't route at least one of its routes
// (e.g. no empty available in inventory). The solver reports this as a feasible:false
// route whose cost is the ~1e9 infeasibility marker, which inflates the plan's whole
// objective — so the plan's `cost` is meaningless and must not be shown as a dollar
// figure. Helpers below drive the "hide the bogus cost, show the reason" UI.
export function planIsInfeasible(plan?: OptimizedPlan | null): boolean {
  return !!plan && (plan.routes ?? []).some((r) => !r.feasible);
}

// The best (first) option is what the list and result default to; a run is infeasible
// when that option is.
export function runIsInfeasible(run: FleetOptimization): boolean {
  return planIsInfeasible(run.result?.options?.[0]);
}

// Human-readable reason per infeasible route, in route order (empty when feasible).
export function planInfeasibilityDetails(plan?: OptimizedPlan | null): string[] {
  return (plan?.routes ?? [])
    .filter((r) => !r.feasible && r.infeasibility)
    .map((r) => r.infeasibility!.detail);
}

// What accepting a plan wrote (or, for a dry run, would write) for one driver.
export interface AcceptedSchedule {
  driverId: string;
  scheduleId?: string;
  scheduleCreated: boolean; // true = new schedule, false = reused existing
  clearedItems: number; // pre-existing items replaced
  deliveries: number;
  pickups: number;
  dumpStorageStops: number;
}

// Result of POST /fleet_optimizations/:id/accept — applies a ranked plan to the
// real schedule for the run's date (replacing existing items for those drivers).
export interface AcceptResult {
  fleetOptimizationId: string;
  date: string;
  planIndex: number;
  dryRun: boolean;
  schedules: AcceptedSchedule[];
}
